import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { ScoreItem } from "../../../components/ScoreItem";
import {
  HealthIndexResponse,
  HealthScore,
  HealthIndexConditions,
  HEALTH_INDEX_CONDITIONS,
} from "../../../models/HealthIndex";
import DataSvc from "../../../services/dataSvc";

import "./styles.scss";

interface IHealthIndexSectionProps {
  locationId: number;
}

export const HealthIndexSection: React.FC<IHealthIndexSectionProps> = ({
  locationId,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`locationPreferencesPage.healthIndexSection.${key}`);

  const { data: healthIndexData, isLoading } = useQuery<HealthIndexResponse>(
    ["healthIndex", locationId],
    async () => {
      return await DataSvc.getLocationHealthIndex(locationId);
    }
  );

  const renderScoreSection = (score: HealthScore) => {
    const conditions = HEALTH_INDEX_CONDITIONS[score];
    if (!conditions || conditions.length === 0) {
      return null;
    }

    const isCurrent = healthIndexData?.currentHealthIndex === score;

    return (
      <div key={score} className={`score-section${isCurrent ? ` score-current-${score.replace("+", "plus")}` : ""}`}>
        <div className="score-badge">
          <ScoreItem score={score} />
        </div>
        <div className="conditions-list">
          {conditions.map((condition) => {
            if (
              (condition.key === "has_food_menus" ||
                condition.key === "has_service_items") &&
              healthIndexData &&
              healthIndexData.conditions[
                condition.key as keyof HealthIndexConditions
              ] == null
            ) {
              return null;
            }

            if (condition.key === "d_is_default") {
              return (
                <div key={condition.key} className="condition-item">
                  <span className="condition-text">
                    {t(`conditions.${condition.translationKey}`)}
                  </span>
                </div>
              );
            }

            const isMet = healthIndexData
              ? condition.checkFunction(healthIndexData.conditions)
              : null;

            return (
              <div key={condition.key} className="condition-item">
                {!!isMet ? (
                  <CheckCircle2 className="condition-icon success" size={16} />
                ) : (
                  <XCircle className="condition-icon error" size={16} />
                )}
                <span className="condition-text">
                  {t(`conditions.${condition.translationKey}`)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="health-index-section white-border-panel">
      {isLoading && <div className="loading-state">{t("loading")}</div>}
      <div className="scores-container">
        {renderScoreSection(HealthScore.A_PLUS)}
        {renderScoreSection(HealthScore.A)}
        {renderScoreSection(HealthScore.B)}
        {renderScoreSection(HealthScore.C)}
        {renderScoreSection(HealthScore.D)}
      </div>
    </div>
  );
};
